/* eslint-disable no-unused-expressions */
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSearchDictionary } from '../../util';
import { useMenuOpen } from 'lib/util/toggle-menu';
import { classnames } from 'tailwindcss-classnames';
import { useConnector } from 'react-instantsearch-hooks-web';
import 'react-datepicker/dist/react-datepicker.css';
import { SearchWrapperProps } from '../../SearchTypes';
import AnnouncementClearFacets from '../../Facets/NetworkResourceCenter/Announcement/AnnouncementClearFacets';
import Button from 'components/helpers/Button';
import connectStats from 'instantsearch.js/es/connectors/stats/connectStats';
import type {
  StatsConnectorParams,
  StatsWidgetDescription,
} from 'instantsearch.js/es/connectors/stats/connectStats';
import SearchSort from '../../SearchSort';
import ClinicalFacet from '../../Facets/NetworkResourceCenter/ProductList/ClinicalFacet';
import MediaFacet from '../../Facets/NetworkResourceCenter/ProductList/MediaFacet';
import ResourceFacet from '../../Facets/NetworkResourceCenter/ProductList/ResourceFacet';
import OperationalFacet from '../../Facets/NetworkResourceCenter/ProductList/OperationalFacet';
import PatientResourcesFacet from '../../Facets/NetworkResourceCenter/ProductList/PatientResourcesFacet';
import LanguageFacet from '../../Facets/NetworkResourceCenter/ProductList/LanguageFacet';
import { IsNetworkSite } from 'lib/util/site-configuration';

export type UseStatsProps = StatsConnectorParams;

export interface IMyProps {
  filters: string[];
}
function useStats(props?: UseStatsProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useConnector<StatsConnectorParams, StatsWidgetDescription>(connectStats as any, props);
}
const SearchFacetsWrapper = ({ children }: SearchWrapperProps) => {
  return <div className={classnames('mb-4')}>{children}</div>;
};

const SearchFacetsWithSearch = (props: UseStatsProps) => {
  const { nbHits } = useStats(props);
  const { toggleMenu, isMenuOpen } = useMenuOpen();

  const dict = useSearchDictionary();
  const resultsCount = dict.resultsCountNoQuery.replace('@count', nbHits.toString());

  const menuClasses = classnames(
    'bg-black',
    'bg-opacity-30',
    'duration-300',
    'fixed',
    'h-full',
    'w-full',
    'top-0',
    'transition-transform',
    'transform',
    'left-[100%]',
    'lg:relative',
    'lg:bg-theme-bg',
    {
      '-translate-x-full': isMenuOpen,
      'translate-x-0': !isMenuOpen,
      'lg:left-0': !isMenuOpen,
    }
  );
  return (
    <SearchFacetsWrapper>
      <div className={classnames('block', 'lg:hidden')}>
        <Button
          variant="secondary"
          className={classnames('mb-6', 'w-full', 'z-20')}
          onClick={toggleMenu}
        >
          Filter & Sort
        </Button>
        <hr className={classnames('mb-6', 'border-gray-midGray')} />
        <AnnouncementClearFacets />
      </div>
      <div className={menuClasses}>
        <div
          className={classnames(
            'absolute',
            'border',
            'border-gray-midGray',
            'box-border',
            'overflow-scroll',
            'h-full',
            'min-w-[200px]',
            'max-w-2xl',
            'py-6',
            'w-fit',
            'w-full',
            'top-0',
            'right-0',
            'bg-white',
            'lg:relative',
            'lg:rounded-xl',
            'lg:min-w-full',
            'lg:overflow-hidden',
            'z-20'
          )}
        >
          <div className={classnames('flex', 'flex-col', 'lg:hidden', 'px-4')}>
            <Button
              variant="link"
              className={classnames('mb-6', 'z-20', 'text-right')}
              onClick={toggleMenu}
            >
              <span className={classnames('text-black')}>Close filters ✕</span>
            </Button>
            <Button
              variant="primary"
              className={classnames('mb-6', 'z-20', 'block', 'lg:hidden')}
              onClick={toggleMenu}
            >
              {resultsCount}
            </Button>
            <SearchSort profile="product-list" />
          </div>
          <hr className={classnames('my-4', 'border-4', 'border-gray-whisper', 'lg:hidden')} />
          <div
            className={classnames(
              'w-full',
              'justify-between',
              'items-center',
              'hidden',
              'lg:block',
              'px-4'
            )}
          >
            <AnnouncementClearFacets />
          </div>
          <div className={classnames('w-full', 'px-4', 'justify-between')}>
            <MediaFacet />
            <hr className={classnames('mb-4', 'border-gray-midGray')} />
            <ResourceFacet />
            <hr className={classnames('mb-4', 'border-gray-midGray')} />
            <PatientResourcesFacet />
            {IsNetworkSite() && (
              <>
                <hr className={classnames('mb-4', 'border-gray-midGray')} />
                <ClinicalFacet />
                <hr className={classnames('mb-4', 'border-gray-midGray')} />
                <OperationalFacet />
              </>
            )}
            <hr className={classnames('mb-4', 'border-gray-midGray')} />
            <LanguageFacet />
          </div>
        </div>
      </div>
    </SearchFacetsWrapper>
  );
};

const ProductListFacets = () => {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;
  if (pageEditing) {
    return (
      <SearchFacetsWrapper>
        <div className={classnames('mb-6', 'flex', 'w-full', 'justify-between', 'items-center')}>
          <h3 className={classnames('text-blue-navy', 'heading-md')}>Product List Facets</h3>
        </div>
      </SearchFacetsWrapper>
    );
  }
  return <SearchFacetsWithSearch />;
};

export default ProductListFacets;
